import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowBack,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import ErpDataGrid from '../../components/common/ErpDataGrid';
import ContactFormDialog from '../../components/ContactFormDialog';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

/**
 * Contacts screen — global (/contacts) or account-scoped (/accounts/:accountId/contacts).
 * Contacts are always associated with a company (Account).
 */
const ContactsList = () => {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const scoped = Boolean(accountId);
  const { can } = usePermissions();
  const canAdd = can('accounts', 'add');
  const canEdit = can('accounts', 'edit');
  const canDelete = can('accounts', 'delete');

  const [account, setAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterAccountId, setFilterAccountId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await api.get('/accounts', { params: { page: 1, limit: 500 } });
      setAccounts(res.data?.data || []);
    } catch {
      setAccounts([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 500 };
      if (search) params.search = search;
      const effectiveAccountId = scoped ? accountId : filterAccountId;
      if (effectiveAccountId) params.account_id = effectiveAccountId;

      const requests = [api.get('/contacts', { params })];
      if (scoped) requests.push(api.get(`/accounts/${accountId}`));

      const [contactsRes, accountRes] = await Promise.all(requests);
      setRows(contactsRes.data.data || []);
      if (scoped && accountRes) setAccount(accountRes.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load contacts');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, filterAccountId, scoped, search]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete contact "${row.first_name} ${row.last_name || ''}"?`)) return;
    try {
      await api.delete(`/contacts/${row.id}`);
      toast.success('Contact deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete contact');
    }
  };

  const columns = [
    ...(!scoped
      ? [{
        field: 'company',
        headerName: 'Company',
        flex: 1.2,
        minWidth: 180,
        valueGetter: (params) => params.row?.account?.company_name || params.row?.account?.account_name || '—'
      }]
      : []),
    { field: 'first_name', headerName: 'First Name', flex: 1, minWidth: 120 },
    { field: 'last_name', headerName: 'Last Name', flex: 1, minWidth: 120 },
    { field: 'email', headerName: 'Email', flex: 1.3, minWidth: 200 },
    { field: 'cell_number', headerName: 'Cell', flex: 1, minWidth: 130 },
    { field: 'office_direct_number', headerName: 'Office', flex: 1, minWidth: 130 },
    { field: 'role_title', headerName: 'Role', flex: 1, minWidth: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Button
            size="small"
            onClick={() => navigate(
              `/tenants/new?account_id=${row.account_id || accountId}&contact_id=${row.id}`
            )}
          >
            New Tenant
          </Button>
          {canEdit && (
            <IconButton onClick={() => { setEditing(row); setDialogOpen(true); }}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton onClick={() => onDelete(row)}>
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      )
    }
  ];

  const title = scoped
    ? `Contacts — ${account?.company_name || account?.account_name || 'Account'}${account?.account_code ? ` (${account.account_code})` : ''}`
    : 'Contacts';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {scoped ? (
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/accounts')}>Back</Button>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Contacts are stored with their company (Account). Use them when adding tenants.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
        {canAdd && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => { setEditing(null); setDialogOpen(true); }}
          >
            Add Contact
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearch(searchInput.trim());
          }}
          sx={{ minWidth: 240, flex: '1 1 220px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        {!scoped && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Company</InputLabel>
            <Select
              label="Company"
              value={filterAccountId}
              onChange={(e) => setFilterAccountId(e.target.value)}
            >
              <MenuItem value="">All companies</MenuItem>
              {accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.company_name || a.account_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button variant="outlined" onClick={() => setSearch(searchInput.trim())}>Search</Button>
      </Paper>

      <Paper sx={{ height: 620 }}>
        <ErpDataGrid rows={rows} columns={columns} loading={loading} disableRowSelectionOnClick />
      </Paper>

      <ContactFormDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={() => fetchData()}
        editing={editing}
        accountId={scoped ? accountId : (editing?.account_id || filterAccountId || '')}
        lockAccount={scoped}
        accounts={accounts}
      />
    </Box>
  );
};

export default ContactsList;
